import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useUpdateEffect } from 'react-use';
import { toast } from 'react-toastify';
import { keyBy, mapValues } from 'lodash-es';
import { useShallow } from 'zustand/react/shallow';
import { HubsDataDictionary, RegionsDataDictionary, ViolationHighlightData } from '@lp/features/Scheduler/types';
import { getFilteredResources, getResourceShiftTotalsMap } from '@lp/features/Scheduler/utils';
import { ScheduleControlsConfig, useScheduleControls } from '@lp/features/Scheduler/hooks';
import {
  transformCountrySupportViolations,
  transformHubAndRegionViolations,
  transformResourcesViolations,
} from '@lp/features/Scheduler/validation.utils';
import {
  createSchedulerDataHook,
  createSchedulerMutationActionsHook,
  createWorkdaySyncHook,
  createUseShiftChangeHook,
  createUseUnblockShiftHook,
} from '@lp/features/Scheduler/factories';
import {
  getCSHubToResourceMap,
  getCSResourceEmptyShift,
  getShiftOptionFromShift,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/utils';
import {
  getCSNextDate,
  getCSPrevDate,
  getCSStartDay,
  getCSTotalsRange,
  getCSWindowDateRangeLabel,
  getCSWindowLabel,
  getCSWindowRange,
  isSameCSWindow,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/period.utils';
import {
  scheduleFiltersSelectors,
  useCSSchedulerFiltersStore,
  useCSSchedulerHighlightStore,
  useCSSchedulerStore,
} from '@lp/core/store/scheduler';
import { EmployeeRole } from '@/api/apis/resources';
import {
  CSResourcesWithShifts,
  PlanningProcess,
  PlanningProcessStatus,
  CSResource,
  Region,
  Hub,
} from '@/api/apis/resources/schedule';
import {
  useCSCancelGenerationPlanningMutation,
  useCSCancelPlanningMutation,
  useCSCreatePlanningMutation,
  useCSHrSystemConflictResolveMutation,
  useCSHrSystemConflictsQuery,
  useCSMonthScheduleQuery,
  useCSPlanningProcessQuery,
  useCSPublishPlanningMutation,
  useCSRegeneratePlanningMutation,
  useCSScheduleRegionsQuery,
  useCSScheduleResetShiftTypeOptimisticMutation,
  useCSScheduleSetShiftTypeOptimisticMutation,
  useCSScheduleShiftsQuery,
  useCSScheduleSyncWorkdayMutation,
  useCSValidationQuery,
  useCSSetFixationTypeOptimisticMutation,
} from '@/queries/resource/schedule';
import { getFullName } from '@/utils/helpers';

/**
 * Navigation model for the CS schedule. Uses 28-day periods on/after the
 * migration date and falls back to calendar months before it. All other
 * schedules keep the default month-based navigation.
 */
const csScheduleControlsConfig: ScheduleControlsConfig = {
  getStartDay: getCSStartDay,
  getDateRange: getCSWindowRange,
  getPrevDate: getCSPrevDate,
  getNextDate: getCSNextDate,
};

export const useCSScheduleControls = () => {
  const controls = useScheduleControls(csScheduleControlsConfig);
  const periodLabel = useMemo(() => getCSWindowLabel(controls.selectedDate), [controls.selectedDate]);
  const periodDateRange = useMemo(() => getCSWindowDateRangeLabel(controls.selectedDate), [controls.selectedDate]);

  return { ...controls, periodLabel, periodDateRange };
};

export const useCSSchedulerData = createSchedulerDataHook({
  useMonthScheduleQuery: useCSMonthScheduleQuery,
  usePlanningProcessQuery: useCSPlanningProcessQuery,
  useScheduleShiftsQuery: useCSScheduleShiftsQuery,
  employeeRole: EmployeeRole.ClinicalSpecialist,
});

export const useCSSchedulerMutationActions = createSchedulerMutationActionsHook({
  useCreatePlanningMutation: useCSCreatePlanningMutation,
  useRegeneratePlanningMutation: useCSRegeneratePlanningMutation,
  useCancelPlanningMutation: useCSCancelPlanningMutation,
  usePublishPlanningMutation: useCSPublishPlanningMutation,
  useCancelGenerationPlanningMutation: useCSCancelGenerationPlanningMutation,
});

export const useCSWorkdaySync = createWorkdaySyncHook({
  useWorkdaySyncMutation: useCSScheduleSyncWorkdayMutation,
  useGetWorkdayConflicts: useCSHrSystemConflictsQuery,
  useHrSystemConflictResolveMutation: useCSHrSystemConflictResolveMutation,
});

export const useCSSchedulerServices = (selectedDay: Date, shiftsData?: CSResourcesWithShifts) => {
  const { data: regionList } = useCSScheduleRegionsQuery();
  const hubToInsertedMap = useCSSchedulerStore(
    useShallow((store) => mapValues(store.hubConfigs, (config) => config.insertedSpecialists ?? [])),
  );

  const regionsDictionary = useMemo(() => {
    return keyBy(regionList ?? [], 'id');
  }, [regionList]);

  const hubsDictionary = useMemo(() => {
    return keyBy(regionList?.flatMap((r) => r.hubs) ?? [], 'id');
  }, [regionList]);

  const hubToResourceMap = useMemo(() => {
    if (!shiftsData) return null;
    return getCSHubToResourceMap(shiftsData, hubToInsertedMap);
  }, [shiftsData, hubToInsertedMap]);

  const resourceShiftTotalsMap = useMemo(() => {
    if (!shiftsData) return undefined;
    return getResourceShiftTotalsMap(selectedDay, shiftsData, getShiftOptionFromShift, getCSTotalsRange);
  }, [selectedDay, shiftsData]);

  return {
    regionList,
    regionsDictionary,
    hubsDictionary,
    hubToResourceMap,
    resourceShiftTotalsMap,
  };
};

export const useCSShiftChange = createUseShiftChangeHook({
  useScheduleSetShiftTypeOptimisticMutation: useCSScheduleSetShiftTypeOptimisticMutation,
  useScheduleResetShiftTypeOptimisticMutation: useCSScheduleResetShiftTypeOptimisticMutation,
});

export const useCSUnblockShift = createUseUnblockShiftHook({
  useSetFixationTypeOptimisticMutation: useCSSetFixationTypeOptimisticMutation,
});

export const useCSScheduleValidation = (
  planningData?: PlanningProcess,
  shiftsData?: CSResourcesWithShifts,
  hubsDictionary?: HubsDataDictionary,
  regionsDictionary?: RegionsDataDictionary,
  enabled?: boolean,
  withSupportViolations = true,
) => {
  const { scheduleId, status } = planningData ?? {};
  const isEditing = status === PlanningProcessStatus.Editing;
  const canValidate = Boolean(enabled && scheduleId && isEditing);
  const { data, isFetching } = useCSValidationQuery(scheduleId, canValidate);
  const fillingState = data?.scheduleSize.fillingState;

  const { byHubMap, byRegionMap, hubsMessagesList, regionsMessagesList } = useMemo(() => {
    return transformHubAndRegionViolations(data, hubsDictionary, regionsDictionary);
  }, [data, hubsDictionary, regionsDictionary]);

  const { byResourceMap, resourcesMessagesList } = useMemo(() => {
    return transformResourcesViolations(data?.resourceRulesViolations, shiftsData);
  }, [data?.resourceRulesViolations, shiftsData]);

  const supportMessagesList = useMemo(() => {
    return withSupportViolations ? transformCountrySupportViolations(data) : [];
  }, [data, withSupportViolations]);

  const count = useMemo(() => {
    const allMessages = [
      ...hubsMessagesList,
      ...regionsMessagesList,
      ...resourcesMessagesList,
      ...supportMessagesList,
    ].flatMap((v) => v.messages);
    return allMessages.length;
  }, [hubsMessagesList, regionsMessagesList, resourcesMessagesList, supportMessagesList]);

  if (!enabled) return undefined;

  return {
    count,
    byHubMap,
    byRegionMap,
    byResourceMap,
    hubsMessagesList,
    regionsMessagesList,
    resourcesMessagesList,
    supportMessagesList,
    isFetching,
    fillingState,
    publishReadiness: data?.publishReadiness,
  };
};

export const useCSSchedulerHubExpand = (
  regionList: Region[] | undefined,
  hubsDictionary: Record<string, Hub>,
  navigationHubId: string | null,
) => {
  const setHubExpand = useCSSchedulerStore.use.setHubExpand();
  const setRegionExpand = useCSSchedulerStore.use.setRegionExpand();

  const expandHub = useCallback(
    (hubId: string) => {
      const regionId = hubsDictionary[hubId]?.regionId;
      setRegionExpand(regionId, true);
      setTimeout(() => setHubExpand(hubId, true), 100);
    },
    [hubsDictionary, setHubExpand, setRegionExpand],
  );

  useUpdateEffect(() => {
    if (regionList && navigationHubId) expandHub(navigationHubId);
  }, [regionList, navigationHubId, expandHub]);

  return { expandHub };
};

type UseCSSchedulerShiftHighlightProps = {
  selectedDate: Date;
  daysInterval: Date[];
  hubToResourceMap: Map<string, CSResource[]> | null;
  shiftsData?: CSResourcesWithShifts;
  expandHub: (hubId: string) => void;
};
export const useCSSchedulerShiftHighlight = ({
  selectedDate,
  daysInterval,
  hubToResourceMap,
  shiftsData,
  expandHub,
}: UseCSSchedulerShiftHighlightProps) => {
  const setHighlightValue = useCSSchedulerHighlightStore.use.setHighlightValue();

  const highlightResourceEmptyShift = useCallback(
    (resourceId: string) => {
      if (!shiftsData || !hubToResourceMap) return;
      const currentMonthDays = daysInterval.filter((d) => isSameCSWindow(d, selectedDate));
      const obj = getCSResourceEmptyShift(resourceId, currentMonthDays, shiftsData, hubToResourceMap);

      if (obj && obj.hubId && obj.resourceId) {
        expandHub(obj.hubId);
        setHighlightValue(obj);
      } else {
        const resource = shiftsData[resourceId]?.resource;
        const fullName = getFullName(resource?.firstName, resource?.lastName);
        toast.warning(`${fullName} cannot be displayed in the schedule`);
      }
    },
    [daysInterval, expandHub, hubToResourceMap, selectedDate, setHighlightValue, shiftsData],
  );

  const highlightByViolation = useCallback(
    (data: ViolationHighlightData) => {
      let hubId = data?.hubId;
      if (data.resourceId && !data.hubId) hubId = shiftsData?.[data.resourceId]?.resource?.hubId;
      if (hubId && (data.resourceId || data.day)) {
        expandHub(hubId);
        setHighlightValue({ hubId, resourceId: data.resourceId, day: data.day });
      }
    },
    [expandHub, setHighlightValue, shiftsData],
  );

  return { highlightByViolation, highlightResourceEmptyShift };
};

type HubVisibility = {
  /** Set of hub IDs that have users after filtering */
  visibleHubIds: Set<string>;
  /** Set of region IDs that have at least one visible hub */
  visibleRegionIds: Set<string>;
  /** Whether resource-level filters are currently active */
  hasResourceFilters: boolean;
};

export const useCSFilteredHubVisibility = (
  regionList: Region[] | undefined,
  hubToResourceMap: Map<string, CSResource[]> | null,
): HubVisibility => {
  const hasFilters = useCSSchedulerFiltersStore(scheduleFiltersSelectors.hasResourceFilters);
  const filters = useCSSchedulerFiltersStore(
    useShallow((s) => ({
      resources: s.resources,
      organs: s.organs,
      phoneSupportOrgans: s.phoneSupportOrgans,
      shadowOrgans: s.shadowOrgans,
      proctorOrgans: s.proctorOrgans,
      hubs: s.hubs,
    })),
  );

  const { visibleHubIds, visibleRegionIds } = useMemo(() => {
    const hubIds = new Set<string>();
    const regionIds = new Set<string>();

    if (!hasFilters || !regionList || !hubToResourceMap) {
      // No filters active — all hubs/regions are visible
      regionList?.forEach((region) => {
        regionIds.add(region.id);
        region.hubs.forEach((hub) => hubIds.add(hub.id));
      });
      return { visibleHubIds: hubIds, visibleRegionIds: regionIds };
    }

    regionList.forEach((region) => {
      region.hubs.forEach((hub) => {
        // Apply hubs filter
        if (filters.hubs.length > 0 && !filters.hubs.includes(hub.id)) return;

        const userList = hubToResourceMap.get(hub.id) ?? [];
        const filteredUsers = getFilteredResources(userList, filters);

        if (filteredUsers.length > 0) {
          hubIds.add(hub.id);
          regionIds.add(region.id);
        }
      });
    });

    return { visibleHubIds: hubIds, visibleRegionIds: regionIds };
  }, [hasFilters, regionList, hubToResourceMap, filters]);

  return { visibleHubIds, visibleRegionIds, hasResourceFilters: hasFilters };
};

export const useCSAutoExpandOnFilterChange = (hubVisibility: HubVisibility) => {
  const setRegionExpand = useCSSchedulerStore.use.setRegionExpand();
  const setHubExpand = useCSSchedulerStore.use.setHubExpand();

  const prevVisibleRef = useRef<{ regionIds: Set<string>; hubIds: Set<string> }>({
    regionIds: new Set(),
    hubIds: new Set(),
  });

  const { hasResourceFilters, visibleRegionIds, visibleHubIds } = hubVisibility;

  useEffect(() => {
    if (!hasResourceFilters) {
      prevVisibleRef.current = { regionIds: new Set(), hubIds: new Set() };
      return;
    }

    const prev = prevVisibleRef.current;

    visibleRegionIds.forEach((regionId) => {
      if (!prev.regionIds.has(regionId)) setRegionExpand(regionId, true);
    });
    visibleHubIds.forEach((hubId) => {
      if (!prev.hubIds.has(hubId)) setHubExpand(hubId, true);
    });

    prevVisibleRef.current = { regionIds: visibleRegionIds, hubIds: visibleHubIds };
  }, [hasResourceFilters, visibleRegionIds, visibleHubIds, setRegionExpand, setHubExpand]);
};
